/**
 * Lumo Upsell Service
 * Masterful, contextual upsells that increase retention and upgrades
 * Makes premium features irresistible through value-first messaging
 */

import { supabase } from '../lib/supabase';

class LumoUpsellService {
  constructor() {
    // Track upsell attempts to avoid being pushy
    this.sessionUpsells = new Set();
    this.lastUpsellTime = null;
  }

  /**
   * Determine if it's appropriate to show an upsell now
   * @param {string} context - Current context (feature hit limit, browsing, etc.)
   * @returns {boolean}
   */
  canShowUpsell(context) {
    // Don't spam - minimum 5 minutes between upsells
    if (this.lastUpsellTime && Date.now() - this.lastUpsellTime < 300000) {
      return false;
    }

    // Don't repeat same context in same session
    if (this.sessionUpsells.has(context)) {
      return false;
    }

    return true;
  }

  /**
   * Mark that we showed an upsell
   * @param {string} context
   */
  markUpsellShown(context) {
    this.sessionUpsells.add(context);
    this.lastUpsellTime = Date.now();
  }

  /**
   * Get strategic upsell based on user behavior and context
   * @param {Object} profile - User profile
   * @param {string} context - Current context/trigger
   * @param {Object} stats - User stats (level, streak, etc.)
   * @returns {Object|null} Upsell message with CTA
   */
  getStrategicUpsell(profile, context, stats = {}) {
    const currentPlan = profile?.subscription_plan || 'free';
    const level = profile?.level || 1;
    const streak = stats?.streak || 0;

    // Pro users - upsell to Elite
    if (currentPlan === 'pro') {
      return this.getProToEliteUpsell(context, level, streak);
    }

    // Elite users - reinforce value, no upsell
    if (currentPlan === 'elite' || currentPlan === 'team') {
      return null;
    }

    // Free users - contextual upsells to Pro
    return this.getFreeToProUpsell(context, level, streak);
  }

  /**
   * Upsells for free users → Pro
   */
  getFreeToProUpsell(context, level, streak) {
    const upsells = {
      // Hit daily drill limit
      drill_limit: {
        hook: "🔥 You're crushing it today!",
        pain: `You've completed your free daily drill. But here's the thing—top performers don't stop at one rep. They stack reps daily.`,
        value: `Pro members get **unlimited drills** so they can practice until muscle memory kicks in. That's how you level up 3x faster.`,
        social_proof: `The reps at Level 20+? They're averaging 5-7 drills per day. You've got the drive—unlock the tools.`,
        cta: '🚀 Upgrade to Pro - Unlimited Practice',
        urgency: null,
        tier: 'pro'
      },

      // Tried to generate a script
      script_locked: {
        hook: "✍️ Need a killer script fast?",
        pain: `Writing great sales scripts takes time—and every word matters. One weak line can kill your response rate.`,
        value: `Pro members use **AI Script Lab** to generate battle-tested emails, SMS, voicemails, and pitch scripts in seconds. Personalized for your industry, prospect, and goal.`,
        social_proof: `Pro users report 2x better response rates with AI-generated scripts vs. templates. The AI knows what converts.`,
        cta: '📝 Unlock Script Lab - Upgrade to Pro',
        urgency: null,
        tier: 'pro'
      },

      // Tried to use AI Roleplay
      roleplay_locked: {
        hook: "💡 Smart move checking out AI Roleplay.",
        pain: `Here's what you're missing: realistic prospect conversations that adapt to YOUR responses. It's like having a sparring partner 24/7.`,
        value: `Elite members use this to practice tough conversations *before* they happen. Cold calls, discovery, demos—all without blowing real deals.`,
        social_proof: `Reps who roleplay 3x/week see 40% higher close rates. That's not luck—that's preparation.`,
        cta: '🎯 Unlock AI Roleplay - Upgrade to Elite',
        urgency: 'Limited slots available at current pricing',
        tier: 'elite'
      },

      // Tried conversation analyzer
      analyzer_locked: {
        hook: "📊 Want to know what's killing your deals?",
        pain: `You can't fix what you can't see. Most reps lose deals and never know why—was it their tonality? Discovery questions? Close timing?`,
        value: `Elite members upload call recordings and get **AI breakdown**: talk ratio, objection handling score, missed opportunities, exact timestamps where you could've pivoted.`,
        social_proof: `This feature alone has helped reps recover 20%+ of lost pipeline. One analysis = game-changing insights.`,
        cta: '🧠 Unlock Conversation AI - Upgrade to Elite',
        urgency: null,
        tier: 'elite'
      },

      // High streak, showing dedication
      streak_milestone: streak >= 7 ? {
        hook: `🔥 ${streak}-day streak! You're clearly serious about this.`,
        pain: `You're putting in the work. But are you using pro-level tools? Or grinding with one hand tied behind your back?`,
        value: `Pro unlocks **unlimited drills, script library, objection dojo, micro-lessons**—everything top performers use to stay sharp.`,
        social_proof: `85% of our Level 30+ users are Pro or Elite. They didn't get there on free features alone.`,
        cta: '💪 Upgrade to Pro - Train Like a Pro',
        urgency: `${streak} days of effort deserves pro-level tools`,
        tier: 'pro'
      } : null,

      // Hit level milestone
      level_milestone: level >= 10 ? {
        hook: `🏆 Level ${level}—you're in the top 25% of all users.`,
        pain: `You've proven you can level up. Now imagine how fast you'd climb with **unlimited access** to every training module.`,
        value: `Pro members level up 3x faster because they're not waiting for daily resets. They practice on-demand, whenever inspiration strikes.`,
        social_proof: `Your competition is using Pro features. Don't let artificial limits slow you down.`,
        cta: '⚡ Go Pro - Accelerate Your Growth',
        urgency: 'First month 30% off for Level 10+ users',
        tier: 'pro'
      } : null,

      // Browsing elite features
      exploring_premium: {
        hook: "👀 I see you checking out premium features.",
        pain: `Curiosity is good. But watching from the sidelines doesn't close deals. You need reps—live reps with real scenarios.`,
        value: `Pro gives you **unlimited practice** across every objection, script, and pitch type. Elite adds **AI roleplay + conversation analysis**. Both accelerate your growth 10x.`,
        social_proof: `Free users average 2-3 sessions/month. Pro users? 15-20 sessions. Elite users? Daily. Guess who's hitting quota?`,
        cta: '🚀 See All Plans - Unlock Your Potential',
        urgency: null,
        tier: 'pro'
      },

      // Completed first drill (great experience)
      first_drill_complete: {
        hook: "🎉 First drill done! How'd that feel?",
        pain: `That's just one scenario. But real sales? You face 20+ objection types, 10+ buyer personas, countless variations.`,
        value: `Pro members get **unlimited drills** covering every scenario you'll ever face. Practice until no objection catches you off guard.`,
        social_proof: `The reps who crush it? They've drilled hundreds of reps. You just proved you can do this—now scale it.`,
        cta: '💥 Unlock Unlimited Drills - Go Pro',
        urgency: '7-day trial available',
        tier: 'pro'
      },

      // Struggling with performance
      low_scores: {
        hook: "💭 Tough practice session?",
        pain: `Look, everyone has off days. But if you're consistently struggling, you need better coaching—not more of the same drills.`,
        value: `Pro unlocks **micro-lessons** that target your exact weaknesses. AI analyzes your gaps and serves bite-sized coaching to fix them.`,
        social_proof: `Reps who use micro-lessons see 35% faster skill improvement. Targeted coaching beats random practice every time.`,
        cta: '🎯 Get Personalized Coaching - Upgrade to Pro',
        urgency: null,
        tier: 'pro'
      }
    };

    return upsells[context] || upsells.exploring_premium;
  }

  /**
   * Upsells for Pro users → Elite
   */
  getProToEliteUpsell(context, level, streak) {
    const upsells = {
      // Viewing roleplay from drill
      roleplay_tease: {
        hook: "🎭 Ready to level up from drills to live roleplay?",
        pain: `Drills are great for fundamentals. But real conversations are messy—prospects interrupt, go off-script, throw curveballs.`,
        value: `Elite's **AI Roleplay** simulates real buyers who react to YOUR words. They get defensive if you push. They open up if you listen. It's the closest thing to real reps without risking quota.`,
        social_proof: `Pro users who upgrade to Elite report 50% higher confidence on real calls. Roleplay builds instincts drills can't.`,
        cta: '🚀 Upgrade to Elite - Master Real Conversations',
        urgency: 'Elite-only: AI Roleplay + Conversation Analysis',
        tier: 'elite'
      },

      // High level Pro user
      level_milestone: level >= 25 ? {
        hook: `🏆 Level ${level} on Pro—you're elite-level already.`,
        pain: `You've maxed out Pro features. The only thing holding you back now? Not having elite-level tools.`,
        value: `Elite adds **AI Roleplay** for realistic practice + **Conversation Analyzer** to dissect real calls. These are the tools quota-crushers use to stay ahead.`,
        social_proof: `90% of our Level 40+ users are Elite. They know: to play at elite level, you need elite tools.`,
        cta: '👑 Upgrade to Elite - Join the Top 10%',
        urgency: `You've earned this. Take the final step.`,
        tier: 'elite'
      } : null,

      // Completed lots of drills
      drill_mastery: {
        hook: "💪 You've drilled a TON. Time for the next challenge?",
        pain: `Drills build fundamentals. But mastery? Mastery comes from **dynamic, unpredictable conversations** where you have to think on your feet.`,
        value: `Elite's AI Roleplay throws you into full sales scenarios—cold calls, discovery, negotiation. The AI adapts to you. No two sessions are the same.`,
        social_proof: `Pro users who unlock Elite report this is the game-changer. "Drills got me good. Roleplay made me great."`,
        cta: '🎯 Upgrade to Elite - True Mastery Awaits',
        urgency: null,
        tier: 'elite'
      },

      // Default Pro upsell
      exploring_elite: {
        hook: "👀 Curious about Elite features?",
        pain: `You're already Pro—you know the value of investing in your skills. Elite is the next level.`,
        value: `**AI Roleplay** for realistic practice + **Conversation Analyzer** to review real calls = the ultimate 1-2 punch for closing more deals.`,
        social_proof: `Elite members close 25% more deals on average. ROI happens in one extra close.`,
        cta: '🚀 Upgrade to Elite - Become Unstoppable',
        urgency: 'Most popular upgrade for top performers',
        tier: 'elite'
      }
    };

    return upsells[context] || upsells.exploring_elite;
  }

  /**
   * Format upsell into Lumo chat message
   * @param {Object} upsell - Upsell object
   * @returns {Object} Lumo message format
   */
  formatUpsellMessage(upsell) {
    if (!upsell) return null;

    let message = `${upsell.hook}\n\n`;
    message += `${upsell.pain}\n\n`;
    message += `**Here's what you unlock:**\n${upsell.value}\n\n`;
    
    if (upsell.social_proof) {
      message += `📈 **Real Talk:** ${upsell.social_proof}\n\n`;
    }

    if (upsell.urgency) {
      message += `⏰ ${upsell.urgency}\n\n`;
    }

    message += `Ready to level up?`;

    return {
      type: 'upsell',
      message,
      tier: upsell.tier,
      actions: [
        { 
          label: upsell.cta, 
          type: 'navigate', 
          path: '/subscription',
          trackEvent: `upsell_${upsell.tier}_clicked`
        },
        { 
          label: 'Maybe Later', 
          type: 'dismiss',
          trackEvent: 'upsell_dismissed'
        }
      ]
    };
  }

  /**
   * Track upsell engagement for analytics
   * @param {string} event - Event name
   * @param {Object} metadata - Additional data
   */
  async trackUpsellEvent(event, metadata = {}) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from('analytics_events').insert([{
        user_id: user.id,
        event_type: event,
        metadata,
        created_at: new Date().toISOString()
      }]);
    } catch (error) {
      console.error('Failed to track upsell event:', error);
    }
  }

  /**
   * Get feature preview for locked features (show value before gate)
   * @param {string} feature - Feature name
   * @returns {Object} Preview content
   */
  getFeaturePreview(feature) {
    const previews = {
      objectionDojo: {
        title: '🛡️ Master Every Objection',
        description: 'Practice handling the toughest objections with AI coaching that adapts to YOUR industry and experience level.',
        benefits: [
          'Industry-specific objection library (not generic templates)',
          'AI evaluates your responses in real-time with detailed feedback',
          'Progressive difficulty - start easy, master the hardest objections',
          'Track which objections you\'ve mastered vs. need work',
          'Learn battle-tested frameworks: LUMO, Feel-Felt-Found, Reversal',
          'Practice the same objection multiple ways until perfect'
        ],
        testimonial: '"Price objections used to kill me. After 2 weeks in the Dojo, I handle them confidently and close." - Marcus D., AE',
        cta: 'Unlock Objection Dojo - Go Pro'
      },
      aiRoleplay: {
        title: '🎭 AI Roleplay Preview',
        description: 'Practice realistic sales conversations with AI prospects that adapt to YOUR responses.',
        benefits: [
          'Cold call simulations with realistic gatekeepers',
          'Discovery call practice with dynamic objections',
          'Negotiation scenarios that push back on price',
          'Demo practice with technical questions',
          'Closing conversations with real buying signals'
        ],
        testimonial: '"AI Roleplay tripled my confidence on real calls. The AI feels shockingly real." - Sarah K., AE',
        cta: 'Unlock AI Roleplay - Upgrade to Elite'
      },
      conversationAnalyzer: {
        title: '📊 Conversation Analyzer Preview',
        description: 'Upload call recordings and get AI-powered breakdowns of what worked, what didn\'t, and how to improve.',
        benefits: [
          'Talk ratio analysis (are you listening enough?)',
          'Objection handling scores with improvement tips',
          'Missed opportunity detection with timestamps',
          'Tonality and energy assessment',
          'Buyer engagement signals tracked throughout call'
        ],
        testimonial: '"One analysis showed me I was talking 80% of the time. Fixed that, closed 3 deals that week." - Mike R., SDR',
        cta: 'Unlock Conversation AI - Upgrade to Elite'
      },
      unlimitedDrills: {
        title: '💪 Unlimited Drills Preview',
        description: 'Practice as much as you want, whenever you want. No daily limits.',
        benefits: [
          'All objection types (price, timing, competition, authority)',
          'Multiple difficulty levels that scale with you',
          'Industry-specific scenarios for your market',
          'Custom drill creation (build your own practice)',
          'Performance tracking across all drills'
        ],
        testimonial: '"I drill 30 minutes every morning before calls. My close rate went from 12% to 28%." - James T., BDR',
        cta: 'Unlock Unlimited Practice - Go Pro'
      }
    };

    return previews[feature] || null;
  }
}

export const lumoUpsellService = new LumoUpsellService();
export default lumoUpsellService;
